import { Args, Flags } from '@oclif/core';
import { BaseCommand } from '../../base-command.js';
import { apiGet, apiPost } from '../../lib/api-client.js';
import { executeEval } from '../../lib/eval-executor.js';
import { gradeEvalOutput } from '../../lib/eval-grader.js';

interface EvalTarget {
  id: string;
  name: string;
  type: string;
  content: string;
  version: number;
}

interface Eval {
  id: string;
  name: string;
  prompt: string;
  expectedOutput: string;
  expectations: string[];
}

interface EvalRunResult {
  evalId: string;
  evalTargetId: string;
  evalTargetVersion: number;
  configuration: string;
  runNumber: number;
  iteration: number;
  status: string;
  outputs: Record<string, unknown>;
  timing: Record<string, unknown>;
  grading: Record<string, unknown>;
}

export default class EvalRunCommand extends BaseCommand {
  static override description = 'Run evals against a target, optionally grade results, and post to API';

  static override examples = [
    '<%= config.bin %> evals run <target-id>',
    '<%= config.bin %> evals run <target-id> --parallel 3 --runs-per-eval 2',
    '<%= config.bin %> evals run <target-id> --skip-baseline --skip-grading',
  ];

  static override args = {
    targetId: Args.string({ description: 'Eval target ID', required: true }),
  };

  static override flags = {
    parallel: Flags.integer({ description: 'Number of parallel eval executions', default: 1 }),
    'runs-per-eval': Flags.integer({ description: 'Number of runs per eval for variance analysis', default: 1 }),
    iteration: Flags.integer({ description: 'Iteration number for tracking', default: 1 }),
    'skip-baseline': Flags.boolean({ description: 'Skip baseline (without_target) runs', default: false }),
    'skip-grading': Flags.boolean({ description: 'Skip LLM grading step', default: false }),
    model: Flags.string({ description: 'Model for eval execution (default: user configured)' }),
    'grader-model': Flags.string({ description: 'Model for grading (default: same as execution model)' }),
    timeout: Flags.integer({ description: 'Timeout per eval in ms', default: 120_000 }),
  };

  async run(): Promise<void> {
    const { args, flags } = await this.parse(EvalRunCommand);
    this.requireAuth();

    // Fetch target and its evals
    const target = await apiGet<EvalTarget>(`/api/eval-targets/${args.targetId}`);
    const evals = await apiGet<Eval[]>(`/api/eval-targets/${args.targetId}/evals`);

    if (evals.length === 0) {
      this.error(`No evals found for target "${target.name}". Create some with \`rulemetric evals create\`.`);
    }

    this.log(`Running ${evals.length} eval(s) for "${target.name}" v${target.version}`);
    this.log(`Config: ${flags['runs-per-eval']} run(s) per eval, parallel=${flags.parallel}, iteration=${flags.iteration}`);
    if (flags['skip-baseline']) this.log('Skipping baseline runs');
    if (flags['skip-grading']) this.log('Skipping grading');
    this.log('');

    const configurations: Array<{ name: string; content?: string }> = [
      { name: 'with_target', content: target.content },
    ];
    if (!flags['skip-baseline']) {
      configurations.push({ name: 'without_target' });
    }

    // Build the work queue
    interface WorkItem {
      eval_: Eval;
      configuration: string;
      targetContent?: string;
      runNumber: number;
    }
    const workQueue: WorkItem[] = [];

    for (const eval_ of evals) {
      for (const config of configurations) {
        for (let run = 1; run <= flags['runs-per-eval']; run++) {
          workQueue.push({
            eval_: eval_,
            configuration: config.name,
            targetContent: config.content,
            runNumber: run,
          });
        }
      }
    }

    // Execute with concurrency control
    const results: EvalRunResult[] = [];
    const concurrency = flags.parallel;

    for (let i = 0; i < workQueue.length; i += concurrency) {
      const batch = workQueue.slice(i, i + concurrency);
      const batchResults = await Promise.all(
        batch.map((item) => this.executeAndGrade(item, target, flags)),
      );
      results.push(...batchResults);
    }

    // Post results to API
    this.log('');
    this.log('Posting results to API...');

    for (const result of results) {
      await apiPost('/api/eval-runs', result);
    }

    // Summary
    this.log('');
    this.log('=== Results Summary ===');

    for (const config of configurations) {
      const configResults = results.filter((r) => r.configuration === config.name);
      const completedRuns = configResults.filter((r) => r.status === 'completed');
      const gradedRuns = completedRuns.filter((r) => Object.keys(r.grading).length > 0);

      this.log(`\n${config.name}:`);
      this.log(`  Runs: ${completedRuns.length}/${configResults.length} completed`);

      if (gradedRuns.length > 0) {
        const passRates = gradedRuns
          .map((r) => (r.grading as Record<string, any>).summary?.pass_rate ?? 0);
        const avgPassRate = passRates.reduce((a: number, b: number) => a + b, 0) / passRates.length;
        this.log(`  Avg pass_rate: ${(avgPassRate * 100).toFixed(1)}%`);
      }

      for (const r of configResults) {
        const evalName = evals.find((e) => e.id === r.evalId)?.name ?? r.evalId;
        const passRate = (r.grading as Record<string, any>).summary?.pass_rate;
        const passRateStr = passRate !== undefined ? ` pass_rate=${(passRate * 100).toFixed(0)}%` : '';
        const timeStr = (r.timing as Record<string, any>).duration_ms ? ` ${((r.timing as Record<string, any>).duration_ms / 1000).toFixed(1)}s` : '';
        this.log(`  [${r.status}] ${evalName} run#${r.runNumber}${passRateStr}${timeStr}`);
      }
    }

    if (!flags['skip-baseline'] && results.length > 0) {
      const withTarget = results.filter((r) => r.configuration === 'with_target' && Object.keys(r.grading).length > 0);
      const withoutTarget = results.filter((r) => r.configuration === 'without_target' && Object.keys(r.grading).length > 0);

      if (withTarget.length > 0 && withoutTarget.length > 0) {
        const avgWith = withTarget.map((r) => (r.grading as Record<string, any>).summary?.pass_rate ?? 0).reduce((a: number, b: number) => a + b, 0) / withTarget.length;
        const avgWithout = withoutTarget.map((r) => (r.grading as Record<string, any>).summary?.pass_rate ?? 0).reduce((a: number, b: number) => a + b, 0) / withoutTarget.length;
        const delta = avgWith - avgWithout;
        this.log(`\nDelta: ${delta > 0 ? '+' : ''}${(delta * 100).toFixed(1)}% (with_target vs without_target)`);
      }
    }
  }

  private async executeAndGrade(
    item: { eval_: Eval; configuration: string; targetContent?: string; runNumber: number },
    target: EvalTarget,
    flags: { 'skip-grading': boolean; model?: string; 'grader-model'?: string; timeout: number; iteration: number },
  ): Promise<EvalRunResult> {
    const { eval_, configuration, targetContent, runNumber } = item;

    this.log(`[${configuration}] ${eval_.name} run#${runNumber} — executing...`);

    // Execute the eval
    const execResult = await executeEval({
      prompt: eval_.prompt,
      targetContent,
      timeout: flags.timeout,
      model: flags.model,
    });

    if (execResult.error) {
      this.log(`[${configuration}] ${eval_.name} run#${runNumber} — FAILED: ${execResult.error}`);
      return {
        evalId: eval_.id,
        evalTargetId: target.id,
        evalTargetVersion: target.version,
        configuration,
        runNumber,
        iteration: flags.iteration,
        status: 'failed',
        outputs: { error: execResult.error },
        timing: { duration_ms: execResult.durationMs, total_tokens: execResult.totalTokens },
        grading: {},
      };
    }

    this.log(`[${configuration}] ${eval_.name} run#${runNumber} — completed in ${(execResult.durationMs / 1000).toFixed(1)}s`);

    // Grade if not skipped and expectations exist
    let grading: Record<string, unknown> = {};

    if (!flags['skip-grading'] && eval_.expectations.length > 0) {
      this.log(`[${configuration}] ${eval_.name} run#${runNumber} — grading...`);

      const gradingResult = await gradeEvalOutput({
        prompt: eval_.prompt,
        output: execResult.output,
        expectations: eval_.expectations,
        model: flags['grader-model'] ?? flags.model,
        timeout: flags.timeout,
      });

      grading = gradingResult as unknown as Record<string, unknown>;
      const passRate = gradingResult.summary.pass_rate;
      this.log(`[${configuration}] ${eval_.name} run#${runNumber} — grading done: pass_rate=${(passRate * 100).toFixed(0)}%`);
    }

    return {
      evalId: eval_.id,
      evalTargetId: target.id,
      evalTargetVersion: target.version,
      configuration,
      runNumber,
      iteration: flags.iteration,
      status: 'completed',
      outputs: { transcript: execResult.output },
      timing: {
        duration_ms: execResult.durationMs,
        total_tokens: execResult.totalTokens,
        total_duration_seconds: execResult.durationMs / 1000,
      },
      grading,
    };
  }
}
